'use client'

import { useSession } from 'next-auth/react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData, QueryKey } from '@tanstack/react-query'

import type { FeedItem, FeedItemLikes } from '@/features/profiles/types/feed'
import {
  getFeedItemKey,
  getFeedItemReactable
} from '@/features/profiles/types/feed'
import type { FeedPageResponse } from '@/features/social/hooks/useSocialFeedInfinite'
import { api } from '@/shared/services/api'

type FeedListQueryData = { records: FeedItem[]; pagination?: unknown }

type LikeToggleContext = {
  previousInfinite: InfiniteData<FeedPageResponse> | undefined
  feedSnapshots: Array<[QueryKey, FeedListQueryData | undefined]>
}

function toggleLikesOptimistic(
  likes: FeedItemLikes,
  viewer: { id: string; uid: string; name: string }
): FeedItemLikes {
  const was = likes.liked_by_me
  const count = Math.max(0, likes.count + (was ? -1 : 1))
  const liked_by_me = !was
  let preview_users = [...likes.preview_users]

  if (liked_by_me && !was) {
    const self = {
      id: Number.parseInt(viewer.id, 10) || 0,
      uid: viewer.uid,
      name: viewer.name
    }
    preview_users = [self, ...preview_users.filter(p => p.uid !== viewer.uid)].slice(
      0,
      3
    )
  } else {
    preview_users = preview_users.filter(p => p.uid !== viewer.uid)
  }

  return { count, liked_by_me, preview_users }
}

function patchFeedItem(item: FeedItem, viewer: { id: string; uid: string; name: string }): FeedItem {
  const base: FeedItemLikes = item.likes ?? {
    count: 0,
    liked_by_me: false,
    preview_users: []
  }
  return { ...item, likes: toggleLikesOptimistic(base, viewer) }
}

function patchInfiniteFeed(
  data: InfiniteData<FeedPageResponse> | undefined,
  itemKey: string,
  viewer: { id: string; uid: string; name: string }
): InfiniteData<FeedPageResponse> | undefined {
  if (!data) return data
  return {
    pageParams: data.pageParams,
    pages: data.pages.map(page => ({
      ...page,
      records: (page.records ?? []).map(rec =>
        getFeedItemKey(rec) === itemKey ? patchFeedItem(rec, viewer) : rec
      )
    }))
  }
}

function patchFeedListData(
  data: FeedListQueryData | undefined,
  itemKey: string,
  viewer: { id: string; uid: string; name: string }
): FeedListQueryData | undefined {
  if (!data?.records) return data
  return {
    ...data,
    records: data.records.map(rec =>
      getFeedItemKey(rec) === itemKey ? patchFeedItem(rec, viewer) : rec
    )
  }
}

export function useFeedLikeToggle(item: FeedItem) {
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const itemKey = getFeedItemKey(item)

  return useMutation({
    mutationFn: async () => {
      const { reactable_type, reactable_id } = getFeedItemReactable(item)
      await api.post('/user/reactions/toggle', {
        record: {
          reactable_type,
          reactable_id,
          kind: 'like'
        }
      })
    },
    onMutate: async (): Promise<LikeToggleContext> => {
      const viewer = session?.user
      if (!viewer?.uid) {
        return { previousInfinite: undefined, feedSnapshots: [] }
      }

      await queryClient.cancelQueries({ queryKey: ['social-feed', 'infinite'] })
      await queryClient.cancelQueries({ queryKey: ['/user/feed'] })

      const previousInfinite = queryClient.getQueryData<InfiniteData<FeedPageResponse>>([
        'social-feed',
        'infinite'
      ])

      const feedSnapshots = queryClient.getQueriesData<FeedListQueryData>({
        queryKey: ['/user/feed']
      })

      const v = { id: viewer.id, uid: viewer.uid, name: viewer.name }

      queryClient.setQueryData<InfiniteData<FeedPageResponse> | undefined>(
        ['social-feed', 'infinite'],
        old => patchInfiniteFeed(old, itemKey, v)
      )

      feedSnapshots.forEach(([key]) => {
        queryClient.setQueryData<FeedListQueryData | undefined>(key, old =>
          patchFeedListData(old, itemKey, v)
        )
      })

      return { previousInfinite, feedSnapshots }
    },
    onError: (_err, _vars, context) => {
      if (!context) return
      if (context.previousInfinite !== undefined) {
        queryClient.setQueryData(['social-feed', 'infinite'], context.previousInfinite)
      }
      context.feedSnapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['social-feed', 'infinite'] })
      queryClient.invalidateQueries({ queryKey: ['/user/feed'] })
    }
  })
}
